import { FilterChangeEvent, initValues } from './Filter';
import { Button, Form, Input, InputNumber, Select, Tooltip } from 'antd';
import { ClearOutlined, DownOutlined, QuestionCircleOutlined, UpOutlined } from '@ant-design/icons';
import { useTranslate } from '@tolgee/react';
import { useMemoizedFn } from 'ahooks';
import { SnFormField } from './SnFormField';
import { usePlatform } from '../../hooks/usePlatformList';
import { StoreFormField } from './StoreFormField';
import { StyleFilter } from './style';
import FormItem from 'antd/lib/form/FormItem';
import { ApplyDateFormField } from './ApplyDateFormField';
import { TradeDateFormField } from './TradeDateFormField';
import { useMemo, useState } from 'react';
import { tradeReturnListFilterFoldCache } from 'services/cache';
import dayjs, { Dayjs } from 'lib/dayjs';
import { isTiKTokUpperCase } from 'lib/utils/isTikTok';
import { useStatusLabel } from '../StatusHistoryModal';

interface ShopeeReturnFilterProps {
  filterType: 'return' | 'cancel';
  onChange: (e: FilterChangeEvent) => void;
}

const defaultFormValues = {
  ...initValues,
  dateRange: [dayjs(dayjs().subtract(30, 'days').format('YYYY-MM-DD')), dayjs()],
};

export const ShopeeReturnFilter = (props: ShopeeReturnFilterProps) => {
  const { filterType, onChange } = props;
  const { t } = useTranslate();
  const platform = usePlatform()!;
  const [form] = Form.useForm();
  const [fold, setFold] = useState(tradeReturnListFilterFoldCache.get() ?? false);

  const [disabled, setDisabled] = useState(false);

  const [logisticsType, setLogisticsType] = useState<
    'leChatTraceStatus' | 'reverseLeChatTraceStatus'
  >('leChatTraceStatus');
  const [logisticsStatus, setLogisticsStatus] = useState<string[]>([]);

  const handleClearSubmit = useMemoizedFn(() => {
    form.resetFields();
    form.submit();
    setDisabled(false);
    setLogisticsStatus([]);
  });
  const handleSearchSubmit = useMemoizedFn(() => {
    form.submit();
  });
  const handleFormFinish = useMemoizedFn((e: any) => {
    const { sn, hasNote } = e;
    onChange({
      ...e,
      hasNote,
      sn: !sn ? undefined : sn,
    });
  });
  const handleValuesChange = useMemoizedFn((_, values: any) => {
    if ((values.snType === 'orderId' || values.snType === 'returnId') && values.sn) {
      setDisabled(true);
    } else {
      setDisabled(false);
    }
  });
  const handleFold = useMemoizedFn(() => {
    setFold((prev) => {
      tradeReturnListFilterFoldCache.set(!prev);
      return !prev;
    });
  });

  const statusLabel = useStatusLabel();

  const {
    returnReasonSelectOptions,
    operateSelectOptions,
    returnTypeSelectOptions,
    displayStatusSelectOptions,
    logisticsStatusSelectOptions,
  } = useMemo(() => {
    return {
      returnReasonSelectOptions:
        platform !== 'SHOPEE'
          ? [
              { value: 'DID_NOT_RECEIVE_PARCEL', label: t('aide.未收到货物') },
              { value: 'ITEM_ARRIVED_TOO_LATE', label: t('送达太晚') },
              { value: 'RECEIVED_EMPTY_PARCEL', label: t('aide.收到空包裹') },
              { value: 'ITEM_DOESNT_MATCH_DESCRIPTION', label: t('aide.收到货物与介绍不符') },
              { value: 'ITEM_DOESNT_WORK_PROPERLY', label: t('aide.收到货物无法使用') },
              { value: 'MISSING_ITEM', label: t('aide.收到货物少件') },
              { value: 'DAMAGED', label: t('aide.收到货物破损') },
              { value: 'RECEIVED_WRONG_ITEM', label: t('aide.收到货物错误') },
              { value: 'CHANGE_OF_MIND', label: t('aide.改变主意') },
              { value: 'COUNTERFEIT_ITEM', label: t('假冒商品') },
              { value: 'OTHER', label: t('aide.其他') },
            ]
          : [
              'BROKEN_PRODUCTS',
              'CHANGE_MIND',
              'DAMAGED_OTHERS',
              'DIFFERENT_DESCRIPTION',
              'EXPECTATION_FAILED',
              'EXPIRED_PRODUCT',
              'FUNCTIONAL_DMG',
              'ITEM_FAKE',
              'ITEM_MISSING',
              'ITEM_NOT_FIT',
              'LONG_DELIVERY_TIME',
              'NOT_RECEIPT',
              'OUTER_DAMAGED_PACKAGE',
              'PHYSICAL_DMG',
              'SLIGHT_SCRATCH_DENTS',
              'SPILLED_CONTENTS',
              'SUSPICIOUS_PARCEL',
              'USED',
              'WRONG_ADDRESS',
              'WRONG_ITEM',
              'WRONG_ORDER_INFO',
            ].map((value) => ({
              value,
              label: t(`trade_return_reason_${value}`),
            })),
      operateSelectOptions:
        platform === 'LAZADA'
          ? [
              {
                value: 'CONFIRM_RETURN_REFUND',
                label: t('trade.退货退款'),
              },
              {
                value: 'CONFIRM',
                label: t('trade.仅退款'),
              },
              {
                value: 'REFUSE',
                label: t('tableColumn.拒绝'),
              },
            ]
          : isTiKTokUpperCase(platform)
          ? [
              {
                value: 'CONFIRM_RETURN_REFUND',
                label: t('btn_agree_refunds'),
              },
              {
                value: 'FULL_REFUND',
                label: t('btn_agree_only_refund'),
              },
              {
                value: 'CONFIRM_CHANGE',
                label: t('同意换货'),
              },
              {
                value: 'REFUSE',
                label: t('tableColumn.拒绝'),
              },
              {
                value: 'OFFER',
                label: t('部分退款'),
              },
              {
                value: 'RETURN_REVIEW_OK',
                label: t('确认收到包裹'),
              },
            ]
          : [
              {
                value: 'FULL_REFUND',
                label: t('trade.全额退款'),
              },
              {
                value: 'OFFER',
                label: t('trade.提出部分退款'),
              },
              {
                value: 'WAIT_RETURN',
                label: t('trade.等待退货'),
              },
              {
                value: 'SUBMIT_EVIDENCE',
                label: t('trade.上传证据'),
              },
              {
                value: 'DISPUTE_TO_SHOPEE',
                label: t('trade.向Shopee争议'),
              },
            ],
      returnTypeSelectOptions: [
        {
          value: 'ONLY_REFUND',
          label: t('ViewRefund.仅退款'),
        },
        {
          value: 'RETURN_REFUND',
          label: t('ViewRefund.退货退款'),
        },
        ...(isTiKTokUpperCase(platform)
          ? [
              {
                value: 'CHANGE',
                label: t('换货'),
              },
            ]
          : []),
      ],
      displayStatusSelectOptions:
        platform === 'LAZADA'
          ? Object.entries(statusLabel).map(([value, label]) => {
              return {
                value,
                label,
              };
            })
          : isTiKTokUpperCase(platform)
          ? [
              { value: 'REFUND_REQUEST_PENDING', label: t('tiktok.1_ONLY_REFUND') },
              { value: 'RETURN_REQUEST_PENDING', label: t('tiktok.1_RETURN_REFUND') },
              {
                value: 'REPLACEMENT_REQUEST_PENDING',
                label: t('tiktok.REPLACEMENT_REQUEST_PENDING'),
              },
              { value: 'AWAITING_BUYER_SHIP', label: t('tiktok.3') },
              { value: 'BUYER_SHIPPED_ITEM', label: t('tiktok.4') },
              { value: 'REJECT_RECEIVE_PACKAGE', label: t('拒收包裹') },
              { value: 'RETURN_OR_REFUND_REQUEST_SUCCESS', label: t('tiktok.50') },
              {
                value: 'REPLACEMENT_REQUEST_REFUND_SUCCESS',
                label: t('tiktok.REPLACEMENT_REQUEST_REFUND_SUCCESS'),
              },
              { value: 'REQUEST_REJECT', label: t('tiktok.2') },
              { value: 'REQUEST_CANCEL', label: t('tiktok.99') },
              { value: 'REQUEST_COMPLETE', label: t('tiktok.REPLACEMENT_REQUEST_COMPLETE') },
            ]
          : [
              'WAITING_EVIDENCE',
              'RETURN_PROCESSING',
              'VALIDATION_IN_PROGRESS',
              'REFUND_APPROVED',
              'EVIDENCE_SUBMITTED',
              'WAITING_BUYER_RESPONSE',
              'REFUND_REQUESTED',
              'RETURN_IN_PROGRESS',
              'REFUND_COMPLETED',
              'DISPUTE_RESOLVED',
              'COMPENSATION_APPROVED',
              'DISPUTE_REJECT',
              'DISPUTE_WAIT_EVIDENCE',
            ].map((value) => {
              return {
                value,
                label: t(`状态展示枚举.${value}`),
              };
            }),
      logisticsStatusSelectOptions: [
        'LE_CHAT_WAITING_COLLECT',
        'LE_CHAT_WAITING_SEND',
        'LE_CHAT_WAITING_SIGN',
        'LE_CHAT_SIGNED',
        'LE_CHAT_SEND_FAIL',
        'LE_CHAT_PACKAGE_LOST',
      ].map((status) => ({
        value: status,
        label: t(`leChatLogisticsStatus.${status}`),
      })),
    };
  }, [t, platform, statusLabel]);

  const disableDate = useMemoizedFn((current: Dayjs) => {
    const bigThanToday = current.isAfter(dayjs(), 'days');
    const lessThan90 = current.isBefore(dayjs().subtract(90, 'days'), 'days');
    return bigThanToday || lessThan90;
  });

  return (
    <div className="bg-fill-0 flex flex-col p-4">
      <StyleFilter
        form={form}
        initialValues={defaultFormValues}
        onFinish={handleFormFinish}
        onValuesChange={handleValuesChange}
      >
        <div className="filter-list">
          <SnFormField
            platform={platform}
            filterType={filterType}
            validate={false}
            onValuesChange={(values) => handleValuesChange({}, values)}
          />
          <StoreFormField disabled={disabled} platform={platform} filterType={filterType} />
          <FormItem name="reason">
            <Select
              dropdownMatchSelectWidth={false}
              disabled={disabled}
              mode="multiple"
              maxTagCount="responsive"
              placeholder={t('退单原因')}
              className="min-w-[160px]"
              allowClear
              showArrow
              options={returnReasonSelectOptions}
            />
          </FormItem>
          <ApplyDateFormField
            disabled={disabled}
            filterType={filterType}
            form={form}
            disabledDate={disableDate}
          />
        </div>
        {!fold ? (
          <>
            <div className="filter-list">
              <FormItem name="operate">
                <Select
                  dropdownMatchSelectWidth={false}
                  disabled={disabled}
                  mode="multiple"
                  maxTagCount="responsive"
                  placeholder={t('可选操作')}
                  className="min-w-[10vw]"
                  allowClear
                  showArrow
                  options={operateSelectOptions}
                />
              </FormItem>
              <FormItem name="returnType">
                <Select
                  dropdownMatchSelectWidth={false}
                  disabled={disabled}
                  placeholder={t('退单类型')}
                  className="!w-[10vw]"
                  allowClear
                  showArrow
                  options={returnTypeSelectOptions}
                />
              </FormItem>
              <FormItem name="displayStatus">
                <Select
                  dropdownMatchSelectWidth={false}
                  disabled={disabled}
                  mode="multiple"
                  maxTagCount="responsive"
                  placeholder={t('退单状态')}
                  className="min-w-[10vw]"
                  allowClear
                  showArrow
                  options={displayStatusSelectOptions}
                />
              </FormItem>
              <FormItem noStyle name="leChatTraceStatus" />
              <FormItem noStyle name="reverseLeChatTraceStatus" />
              {platform === 'SHOPEE' && (
                <Input.Group compact className="inline-flex !w-[auto] flex-nowrap">
                  <Select
                    style={{ maxWidth: '10vw' }}
                    disabled={disabled}
                    dropdownMatchSelectWidth={false}
                    value={logisticsType}
                    onChange={(logisticsType) => {
                      setLogisticsType(logisticsType);
                      form.setFieldsValue({
                        ...form.getFieldsValue(),
                        leChatTraceStatus: [],
                        reverseLeChatTraceStatus: [],
                        [logisticsType]: logisticsStatus,
                      });
                    }}
                    options={[
                      { value: 'leChatTraceStatus', label: t('正向物流') },
                      { value: 'reverseLeChatTraceStatus', label: t('逆向物流') },
                    ]}
                  />
                  <Select
                    className="min-w-[10vw]"
                    disabled={disabled}
                    dropdownMatchSelectWidth={false}
                    mode="multiple"
                    placeholder={t('请选择')}
                    value={logisticsStatus}
                    onChange={(logisticsStatus) => {
                      setLogisticsStatus(logisticsStatus);
                      form.setFieldsValue({
                        ...form.getFieldsValue(),
                        [logisticsType]: logisticsStatus,
                      });
                    }}
                    options={logisticsStatusSelectOptions}
                  />
                </Input.Group>
              )}
              <div className="inline-flex flex-row items-center">
                <span className="text-nowrap mr-2">
                  {t('申请金额')}
                  <Tooltip title={t('筛选固定货币时，可在站点/店铺选择中选择')}>
                    <QuestionCircleOutlined className="mx-0.5" />
                  </Tooltip>
                  :
                </span>
                <FormItem name="refundAmountStart">
                  <InputNumber
                    disabled={disabled}
                    precision={2}
                    className="!w-[114px]"
                    placeholder={t('最小金额')}
                  />
                </FormItem>
                <span className="px-2">-</span>
                <FormItem name="refundAmountEnd">
                  <InputNumber
                    disabled={disabled}
                    precision={2}
                    className="!w-[114px]"
                    placeholder={t('最大金额')}
                  />
                </FormItem>
              </div>
              {platform !== 'SHOPEE' && (
                <TradeDateFormField disabled={disabled} form={form} disabledDate={disableDate} />
              )}
            </div>
            <div className="filter-list">
              {platform === 'SHOPEE' && (
                <TradeDateFormField disabled={disabled} form={form} disabledDate={disableDate} />
              )}
              <FormItem name="hasNote" valuePropName="checked" className="min-w-[160px]">
                <Select disabled={disabled} placeholder={t('订单备注')} allowClear>
                  <Select.Option value={1}>{t('有备注')}</Select.Option>
                  <Select.Option value={2}>{t('无备注')}</Select.Option>
                </Select>
              </FormItem>
            </div>
          </>
        ) : null}
        <div className="flex gap-4">
          <FormItem noStyle>
            <Button
              type="primary"
              htmlType="submit"
              className="!min-w-32"
              onClick={handleSearchSubmit}
            >
              {t('搜索订单')}
            </Button>
          </FormItem>
          <Button type="link" className="!px-1" onClick={handleClearSubmit}>
            <ClearOutlined />
            {t('todo.清除条件')}
          </Button>
          <Button type="link" className="!px-1" onClick={handleFold}>
            {fold ? (
              <>
                {t('展开所有筛选项')}
                <DownOutlined className="ml-2 text-xs" />
              </>
            ) : (
              <>
                {t('收起筛选项')}
                <UpOutlined className="ml-2 text-xs" />
              </>
            )}
          </Button>
        </div>
      </StyleFilter>
    </div>
  );
};
